import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Save, Plus, Trash2, GripVertical, Eye, EyeOff, ArrowRight, Image, Palette, Award, Star, Heart, Users, Globe, Quote, MapPin, Upload, Link2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ICON_MAP = { Image, Palette, Award, Star, Heart, Users, Globe };

/* ─── Reusable form helpers ─── */
const Section = ({ title, children }) => (
  <div className="bg-card border border-border rounded-lg p-4 sm:p-5 space-y-3">
    <h3 className="font-sans text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">{title}</h3>
    {children}
  </div>
);

const ArrayItem = ({ index, onRemove, children }) => (
  <div className="relative border border-border rounded-lg p-3 bg-background group">
    <div className="flex items-center gap-2 mb-2">
      <GripVertical size={12} className="text-muted-foreground" />
      <span className="font-mono text-[10px] text-muted-foreground">#{index + 1}</span>
      <button type="button" onClick={onRemove}
        className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity text-red-500 hover:text-red-700 p-0.5">
        <Trash2 size={12} />
      </button>
    </div>
    {children}
  </div>
);

/* ─── PREVIEW COMPONENTS ─── */
const HomePreview = ({ c }) => {
  const stats = c.stats || [];
  return (
    <div className="space-y-6">
      {/* Hero */}
      <div className="bg-gradient-to-br from-stone-800 to-stone-900 rounded-lg p-6 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-cover opacity-30"
          style={{ backgroundImage: c.hero_background_image ? `url(${c.hero_background_image})` : 'none' }} />
        <div className="relative z-10 space-y-2">
          <p className="text-[10px] uppercase tracking-widest text-white/60">Hero Section</p>
          <h2 className="font-serif text-xl font-light">Title from Site Settings</h2>
          <div className="mt-3 inline-flex items-center gap-1.5 px-4 py-1.5 bg-accent text-white text-xs rounded-full">
            {c.hero_cta_text || 'View Gallery'} <ArrowRight size={12} />
          </div>
        </div>
      </div>

      {/* Stats */}
      {stats.length > 0 && (
        <div className="border border-border rounded-lg p-4">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-3">Stats Counters</p>
          <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${Math.min(stats.length, 3)}, 1fr)` }}>
            {stats.map((s, i) => {
              const Icon = ICON_MAP[s.icon] || Star;
              return (
                <div key={i} className="text-center p-2 bg-muted/30 rounded">
                  <Icon size={16} className="mx-auto mb-1 text-accent" />
                  <p className="font-serif text-lg font-light">{s.value}{s.suffix}</p>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{s.label}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Featured */}
      <div className="border border-border rounded-lg p-4">
        <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Featured Section</p>
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{c.featured_label || 'Featured Works'}</span>
        <h3 className="font-serif text-lg font-light">{c.featured_title || 'Selected Artworks'}</h3>
      </div>

      {/* About Preview */}
      <div className="border border-border rounded-lg p-4 flex gap-3">
        <div className="w-20 h-20 rounded bg-muted flex-shrink-0 overflow-hidden">
          {c.about_preview_image && <img src={c.about_preview_image} alt="" className="w-full h-full object-cover" />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{c.about_preview_label || 'About the Artist'}</p>
          <h4 className="font-serif text-sm font-light">{c.about_preview_heading || 'A Life Devoted to Art'}</h4>
          <p className="text-[10px] text-muted-foreground line-clamp-2 mt-1">{c.about_preview_text || ''}</p>
          <span className="text-[10px] text-accent mt-1 inline-flex items-center gap-1">
            {c.about_preview_cta || 'Read More'} <ArrowRight size={10} />
          </span>
        </div>
      </div>
    </div>
  );
};

const AboutPreview = ({ c }) => (
  <div className="space-y-5">
    {/* Header */}
    <div>
      <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{c.page_label || 'About the Artist'}</p>
      <h2 className="font-serif text-xl font-light mt-1">{c.page_title || 'Gangadhar Som'}</h2>
      <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{c.page_subtitle || ''}</p>
    </div>

    {/* Portrait + Bio */}
    <div className="flex gap-3">
      <div className="w-20 h-24 bg-muted rounded flex-shrink-0 overflow-hidden">
        {c.portrait_image && <img src={c.portrait_image} alt="" className="w-full h-full object-cover" />}
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="font-serif text-sm font-light mb-1">{c.bio_title || 'Biography'}</h4>
        {(c.bio_paragraphs || []).slice(0, 2).map((p, i) => (
          <p key={i} className="text-[10px] text-muted-foreground line-clamp-2 mb-1">{p}</p>
        ))}
        {(c.bio_paragraphs || []).length > 2 && (
          <p className="text-[10px] text-accent">+{(c.bio_paragraphs || []).length - 2} more paragraphs</p>
        )}
      </div>
    </div>

    {/* Timeline */}
    {(c.timeline || []).length > 0 && (
      <div className="border border-border rounded-lg p-3">
        <h4 className="font-serif text-sm font-light mb-2">{c.journey_title || 'Artistic Journey'}</h4>
        <div className="space-y-2 border-l-2 border-accent/30 pl-3">
          {(c.timeline || []).map((t, i) => (
            <div key={i}>
              <p className="text-[10px] text-accent font-medium">{t.period}</p>
              <p className="text-xs font-medium">{t.title}</p>
              <p className="text-[10px] text-muted-foreground line-clamp-1">{t.content}</p>
            </div>
          ))}
        </div>
      </div>
    )}

    {/* Achievements */}
    {(c.achievements || []).length > 0 && (
      <div className="bg-muted/30 rounded-lg p-3">
        <h4 className="font-serif text-sm font-light mb-2">{c.achievements_title || 'Achievements'}</h4>
        <div className="space-y-1">
          {(c.achievements || []).map((a, i) => (
            <div key={i} className="flex items-start gap-1.5">
              <div className="w-1.5 h-1.5 bg-accent rounded-full mt-1 flex-shrink-0" />
              <p className="text-[10px] text-muted-foreground line-clamp-1">{a}</p>
            </div>
          ))}
        </div>
      </div>
    )}

    {/* Quote */}
    {c.quote_text && (
      <div className="border-l-2 border-accent pl-3 py-1">
        <Quote size={14} className="text-accent mb-1" />
        <p className="text-xs italic text-foreground/80 line-clamp-2">"{c.quote_text}"</p>
        {c.quote_attribution && (
          <p className="text-[10px] text-muted-foreground mt-1">— {c.quote_attribution}</p>
        )}
      </div>
    )}
  </div>
);

const SimplePagePreview = ({ c, pageName }) => (
  <div className="space-y-3">
    <div>
      <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{c.page_label || pageName}</p>
      <h2 className="font-serif text-xl font-light mt-1">{c.page_title || pageName}</h2>
      {c.page_description && (
        <p className="text-xs text-muted-foreground mt-2 leading-relaxed">{c.page_description}</p>
      )}
    </div>
    <div className="border border-dashed border-border rounded-lg p-6 text-center">
      <p className="text-[10px] text-muted-foreground uppercase tracking-wider">
        {pageName === 'gallery' ? 'Artwork grid loads here' :
         pageName === 'exhibitions' ? 'Exhibition cards load here' :
         'Content items load here'}
      </p>
    </div>
  </div>
);

const FooterPreview = ({ c }) => (
  <div className="bg-card border border-border rounded-lg p-4 space-y-3">
    <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Footer Preview</p>
    <div className="grid grid-cols-3 gap-3">
      <div>
        <h4 className="font-serif text-sm font-light mb-1">Gangadhar Som</h4>
        <p className="text-[10px] text-muted-foreground line-clamp-3">{c.about_text || ''}</p>
      </div>
      <div>
        <h4 className="text-[10px] uppercase tracking-wider font-semibold mb-1">Quick Links</h4>
        {['Home', 'About', 'Gallery'].map(l => (
          <p key={l} className="text-[10px] text-muted-foreground">{l}</p>
        ))}
      </div>
      <div>
        <h4 className="text-[10px] uppercase tracking-wider font-semibold mb-1">{c.newsletter_title || 'Newsletter'}</h4>
        <p className="text-[10px] text-muted-foreground">{c.newsletter_text || ''}</p>
        <div className="mt-1 flex gap-1">
          <div className="flex-1 h-5 bg-muted rounded text-[8px] px-1.5 flex items-center text-muted-foreground">email@...</div>
          <div className="w-5 h-5 bg-accent rounded flex items-center justify-center">
            <MapPin size={8} className="text-white" />
          </div>
        </div>
      </div>
    </div>
  </div>
);

const PreviewPanel = ({ activeTab, content }) => {
  const c = content[activeTab] || {};
  const previewMap = {
    home: <HomePreview c={c} />,
    about: <AboutPreview c={c} />,
    gallery: <SimplePagePreview c={c} pageName="Gallery" />,
    exhibitions: <SimplePagePreview c={c} pageName="Exhibitions" />,
    press: <SimplePagePreview c={c} pageName="Press & Media" />,
    footer: <FooterPreview c={c} />,
  };

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border bg-muted/30">
        <Eye size={14} className="text-accent" />
        <span className="font-sans text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">
          Live Preview — {activeTab}
        </span>
        <div className="ml-auto flex gap-1">
          <div className="w-2 h-2 rounded-full bg-red-400" />
          <div className="w-2 h-2 rounded-full bg-yellow-400" />
          <div className="w-2 h-2 rounded-full bg-green-400" />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-4" data-testid="live-preview-content">
        {previewMap[activeTab] || <p className="text-xs text-muted-foreground">No preview available</p>}
      </div>
    </div>
  );
};

/* ─── MAIN COMPONENT ─── */
const PageContentEditor = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('home');
  const [content, setContent] = useState({});
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState({});
  const [showPreview, setShowPreview] = useState(true);
  const [uploadingHero, setUploadingHero] = useState(false);
  const token = localStorage.getItem('admin_token');

  const loadPage = useCallback(async (page) => {
    if (loaded[page]) return;
    try {
      const res = await axios.get(`${API}/page-content/${page}`);
      setContent(prev => ({ ...prev, [page]: res.data }));
      setLoaded(prev => ({ ...prev, [page]: true }));
    } catch (err) {
      console.error(err);
    }
  }, [loaded]);

  useEffect(() => {
    if (!token) { navigate('/admin'); return; }
    loadPage('home');
  }, [token, navigate, loadPage]);

  useEffect(() => { loadPage(activeTab); }, [activeTab, loadPage]);

  const update = (page, field, value) => {
    setContent(prev => ({ ...prev, [page]: { ...prev[page], [field]: value } }));
  };

  const updateArrayItem = (page, field, index, key, value) => {
    setContent(prev => {
      const arr = [...(prev[page]?.[field] || [])];
      if (typeof arr[index] === 'object') {
        arr[index] = { ...arr[index], [key]: value };
      } else {
        arr[index] = value;
      }
      return { ...prev, [page]: { ...prev[page], [field]: arr } };
    });
  };

  const addArrayItem = (page, field, template) => {
    setContent(prev => {
      const arr = [...(prev[page]?.[field] || []), template];
      return { ...prev, [page]: { ...prev[page], [field]: arr } };
    });
  };

  const removeArrayItem = (page, field, index) => {
    setContent(prev => {
      const arr = [...(prev[page]?.[field] || [])];
      arr.splice(index, 1);
      return { ...prev, [page]: { ...prev[page], [field]: arr } };
    });
  };

  const handleSave = async (page) => {
    setSaving(true);
    try {
      await axios.put(`${API}/page-content/${page}`, content[page], {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${page.charAt(0).toUpperCase() + page.slice(1)} page content saved!`);
    } catch (err) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleHeroImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingHero(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      const res = await axios.post(`${API}/upload/hero-image`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      if (res.data.success) {
        update('home', 'hero_background_image', res.data.url);
        toast.success('Hero image uploaded! Click Save to apply.');
      }
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setUploadingHero(false);
    }
  };

  const pg = (page) => content[page] || {};

  return (
    <div className="min-h-screen pt-24 sm:pt-28 pb-16" data-testid="page-content-editor">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 md:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <button onClick={() => navigate('/admin/dashboard')}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-accent mb-1 transition-colors"
              data-testid="back-to-dashboard">
              <ArrowLeft size={14} /> Back to Dashboard
            </button>
            <h1 className="font-serif text-2xl sm:text-3xl md:text-4xl font-light tracking-tight"
              data-testid="page-editor-title">Page Content Editor</h1>
          </div>
          <Button
            variant={showPreview ? "default" : "outline"}
            size="sm"
            onClick={() => setShowPreview(!showPreview)}
            className={showPreview ? "bg-accent hover:bg-accent/90" : ""}
            data-testid="toggle-preview-btn"
          >
            {showPreview ? <Eye size={14} className="mr-1.5" /> : <EyeOff size={14} className="mr-1.5" />}
            {showPreview ? 'Preview On' : 'Preview Off'}
          </Button>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3 sm:grid-cols-6 h-auto mb-4">
            {['home', 'about', 'gallery', 'exhibitions', 'press', 'footer'].map(p => (
              <TabsTrigger key={p} value={p} className="capitalize text-xs py-1.5" data-testid={`tab-${p}`}>{p}</TabsTrigger>
            ))}
          </TabsList>

          {/* Split layout: Form | Preview */}
          <div className={`flex gap-5 ${showPreview ? '' : ''}`}>
            {/* ─── FORM PANEL ─── */}
            <div className={`${showPreview ? 'w-[58%]' : 'w-full'} transition-all duration-300 space-y-0`} data-testid="form-panel">

              {/* HOME TAB */}
              <TabsContent value="home" className="space-y-4 mt-0">
                <Section title="Hero Background">
                  <p className="text-[10px] text-muted-foreground">Upload an image or paste a URL for the homepage hero background. For video backgrounds, use <a href="/admin/customize" className="text-accent underline">Site Customization</a>.</p>
                  <div className="flex gap-3 items-end">
                    <div className="flex-1">
                      <Label className="text-xs">Image URL</Label>
                      <Input value={pg('home').hero_background_image || ''} onChange={e => update('home', 'hero_background_image', e.target.value)}
                        placeholder="https://... or upload below" data-testid="home-hero-bg-url" />
                    </div>
                    <div>
                      <Label className="text-xs sr-only">Upload</Label>
                      <label className="inline-flex items-center gap-1.5 px-3 py-[7px] border border-border rounded-lg text-xs cursor-pointer hover:bg-muted transition-colors"
                        data-testid="home-hero-bg-upload">
                        <Upload size={14} className={uploadingHero ? 'animate-spin' : ''} />
                        {uploadingHero ? 'Uploading...' : 'Upload'}
                        <input type="file" accept="image/*" className="hidden" onChange={handleHeroImageUpload} disabled={uploadingHero} />
                      </label>
                    </div>
                  </div>
                  {pg('home').hero_background_image && (
                    <div className="relative mt-2 rounded-lg overflow-hidden h-32 bg-muted">
                      <img
                        src={pg('home').hero_background_image.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${pg('home').hero_background_image}` : pg('home').hero_background_image}
                        alt="Hero preview" className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                        <span className="text-white text-xs font-sans uppercase tracking-wider">Current Hero Background</span>
                      </div>
                      <button type="button" onClick={() => update('home', 'hero_background_image', '')}
                        className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                        data-testid="remove-hero-bg">
                        <Trash2 size={12} />
                      </button>
                    </div>
                  )}
                </Section>

                <Section title="Hero Call-to-Action">
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div><Label className="text-xs">Button Text</Label>
                      <Input value={pg('home').hero_cta_text || ''} onChange={e => update('home', 'hero_cta_text', e.target.value)}
                        placeholder="View Gallery" data-testid="home-hero-cta-text" /></div>
                    <div><Label className="text-xs">Button Link</Label>
                      <Input value={pg('home').hero_cta_link || ''} onChange={e => update('home', 'hero_cta_link', e.target.value)}
                        placeholder="/gallery" data-testid="home-hero-cta-link" /></div>
                  </div>
                </Section>

                <Section title="Stats Counters">
                  <div className="space-y-2">
                    {(pg('home').stats || []).map((stat, i) => (
                      <ArrayItem key={i} index={i} onRemove={() => removeArrayItem('home', 'stats', i)}>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                          <div><Label className="text-[10px]">Value</Label>
                            <Input type="number" value={stat.value || 0}
                              onChange={e => updateArrayItem('home', 'stats', i, 'value', parseInt(e.target.value) || 0)}
                              data-testid={`stat-value-${i}`} /></div>
                          <div><Label className="text-[10px]">Suffix</Label>
                            <Input value={stat.suffix || ''} onChange={e => updateArrayItem('home', 'stats', i, 'suffix', e.target.value)}
                              placeholder="+" data-testid={`stat-suffix-${i}`} /></div>
                          <div><Label className="text-[10px]">Label</Label>
                            <Input value={stat.label || ''} onChange={e => updateArrayItem('home', 'stats', i, 'label', e.target.value)}
                              data-testid={`stat-label-${i}`} /></div>
                          <div><Label className="text-[10px]">Icon</Label>
                            <select value={stat.icon || 'Image'} onChange={e => updateArrayItem('home', 'stats', i, 'icon', e.target.value)}
                              className="w-full px-2 py-[7px] bg-background border border-border rounded-lg text-xs"
                              data-testid={`stat-icon-${i}`}>
                              {['Image', 'Palette', 'Award', 'Star', 'Heart', 'Users', 'Globe'].map(ic => (
                                <option key={ic} value={ic}>{ic}</option>
                              ))}
                            </select></div>
                        </div>
                      </ArrayItem>
                    ))}
                    <Button type="button" variant="outline" size="sm"
                      onClick={() => addArrayItem('home', 'stats', { value: 0, suffix: '', label: 'New Stat', icon: 'Star' })}
                      data-testid="add-stat-btn">
                      <Plus size={12} className="mr-1" /> Add Stat
                    </Button>
                  </div>
                </Section>

                <Section title="Featured Works Section">
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div><Label className="text-xs">Section Label</Label>
                      <Input value={pg('home').featured_label || ''} onChange={e => update('home', 'featured_label', e.target.value)}
                        data-testid="home-featured-label" /></div>
                    <div><Label className="text-xs">Section Title</Label>
                      <Input value={pg('home').featured_title || ''} onChange={e => update('home', 'featured_title', e.target.value)}
                        data-testid="home-featured-title" /></div>
                  </div>
                </Section>

                <Section title="About Preview Section">
                  <div><Label className="text-xs">Image URL</Label>
                    <Input value={pg('home').about_preview_image || ''} onChange={e => update('home', 'about_preview_image', e.target.value)}
                      placeholder="https://..." data-testid="home-about-image" /></div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div><Label className="text-xs">Label</Label>
                      <Input value={pg('home').about_preview_label || ''} onChange={e => update('home', 'about_preview_label', e.target.value)}
                        data-testid="home-about-label" /></div>
                    <div><Label className="text-xs">CTA Button Text</Label>
                      <Input value={pg('home').about_preview_cta || ''} onChange={e => update('home', 'about_preview_cta', e.target.value)}
                        data-testid="home-about-cta" /></div>
                  </div>
                  <div><Label className="text-xs">Heading</Label>
                    <Input value={pg('home').about_preview_heading || ''} onChange={e => update('home', 'about_preview_heading', e.target.value)}
                      data-testid="home-about-heading" /></div>
                  <div><Label className="text-xs">Description</Label>
                    <Textarea value={pg('home').about_preview_text || ''} onChange={e => update('home', 'about_preview_text', e.target.value)}
                      rows={2} data-testid="home-about-text" /></div>
                </Section>

                <div className="flex justify-end sticky bottom-4 pt-2">
                  <Button onClick={() => handleSave('home')} disabled={saving} className="bg-accent hover:bg-accent/90"
                    data-testid="save-home-btn">
                    <Save size={14} className="mr-1.5" /> {saving ? 'Saving...' : 'Save Home Page'}
                  </Button>
                </div>
              </TabsContent>

              {/* ABOUT TAB */}
              <TabsContent value="about" className="space-y-4 mt-0">
                <Section title="Page Header">
                  <div className="grid sm:grid-cols-3 gap-3">
                    <div><Label className="text-xs">Label</Label>
                      <Input value={pg('about').page_label || ''} onChange={e => update('about', 'page_label', e.target.value)}
                        data-testid="about-label" /></div>
                    <div><Label className="text-xs">Title</Label>
                      <Input value={pg('about').page_title || ''} onChange={e => update('about', 'page_title', e.target.value)}
                        data-testid="about-title" /></div>
                    <div><Label className="text-xs">Portrait Image URL</Label>
                      <Input value={pg('about').portrait_image || ''} onChange={e => update('about', 'portrait_image', e.target.value)}
                        data-testid="about-portrait" /></div>
                  </div>
                  <div><Label className="text-xs">Subtitle</Label>
                    <Textarea value={pg('about').page_subtitle || ''} onChange={e => update('about', 'page_subtitle', e.target.value)}
                      rows={2} data-testid="about-subtitle" /></div>
                </Section>

                <Section title="Biography">
                  <div><Label className="text-xs">Section Title</Label>
                    <Input value={pg('about').bio_title || ''} onChange={e => update('about', 'bio_title', e.target.value)}
                      data-testid="about-bio-title" /></div>
                  <div className="space-y-2">
                    {(pg('about').bio_paragraphs || []).map((para, i) => (
                      <ArrayItem key={i} index={i} onRemove={() => removeArrayItem('about', 'bio_paragraphs', i)}>
                        <Textarea value={para} onChange={e => updateArrayItem('about', 'bio_paragraphs', i, null, e.target.value)}
                          rows={2} data-testid={`about-bio-para-${i}`} />
                      </ArrayItem>
                    ))}
                    <Button type="button" variant="outline" size="sm"
                      onClick={() => addArrayItem('about', 'bio_paragraphs', '')} data-testid="add-bio-para-btn">
                      <Plus size={12} className="mr-1" /> Add Paragraph
                    </Button>
                  </div>
                </Section>

                <Section title="Timeline">
                  <div><Label className="text-xs">Section Title</Label>
                    <Input value={pg('about').journey_title || ''} onChange={e => update('about', 'journey_title', e.target.value)}
                      data-testid="about-journey-title" /></div>
                  <div className="space-y-2">
                    {(pg('about').timeline || []).map((item, i) => (
                      <ArrayItem key={i} index={i} onRemove={() => removeArrayItem('about', 'timeline', i)}>
                        <div className="grid sm:grid-cols-2 gap-2 mb-2">
                          <div><Label className="text-[10px]">Period</Label>
                            <Input value={item.period || ''} onChange={e => updateArrayItem('about', 'timeline', i, 'period', e.target.value)}
                              data-testid={`timeline-period-${i}`} /></div>
                          <div><Label className="text-[10px]">Title</Label>
                            <Input value={item.title || ''} onChange={e => updateArrayItem('about', 'timeline', i, 'title', e.target.value)}
                              data-testid={`timeline-title-${i}`} /></div>
                        </div>
                        <div><Label className="text-[10px]">Content</Label>
                          <Textarea value={item.content || ''} onChange={e => updateArrayItem('about', 'timeline', i, 'content', e.target.value)}
                            rows={2} data-testid={`timeline-content-${i}`} /></div>
                      </ArrayItem>
                    ))}
                    <Button type="button" variant="outline" size="sm"
                      onClick={() => addArrayItem('about', 'timeline', { period: '', title: '', content: '' })}
                      data-testid="add-timeline-btn">
                      <Plus size={12} className="mr-1" /> Add Timeline Entry
                    </Button>
                  </div>
                </Section>

                <Section title="Achievements">
                  <div><Label className="text-xs">Section Title</Label>
                    <Input value={pg('about').achievements_title || ''} onChange={e => update('about', 'achievements_title', e.target.value)}
                      data-testid="about-achievements-title" /></div>
                  <div className="space-y-2">
                    {(pg('about').achievements || []).map((ach, i) => (
                      <ArrayItem key={i} index={i} onRemove={() => removeArrayItem('about', 'achievements', i)}>
                        <Input value={ach} onChange={e => updateArrayItem('about', 'achievements', i, null, e.target.value)}
                          data-testid={`achievement-${i}`} />
                      </ArrayItem>
                    ))}
                    <Button type="button" variant="outline" size="sm"
                      onClick={() => addArrayItem('about', 'achievements', '')} data-testid="add-achievement-btn">
                      <Plus size={12} className="mr-1" /> Add Achievement
                    </Button>
                  </div>
                </Section>

                <Section title="Quote">
                  <div><Label className="text-xs">Quote Text</Label>
                    <Textarea value={pg('about').quote_text || ''} onChange={e => update('about', 'quote_text', e.target.value)}
                      rows={2} data-testid="about-quote-text" /></div>
                  <div><Label className="text-xs">Attribution</Label>
                    <Input value={pg('about').quote_attribution || ''} onChange={e => update('about', 'quote_attribution', e.target.value)}
                      data-testid="about-quote-attr" /></div>
                </Section>

                <div className="flex justify-end sticky bottom-4 pt-2">
                  <Button onClick={() => handleSave('about')} disabled={saving} className="bg-accent hover:bg-accent/90"
                    data-testid="save-about-btn">
                    <Save size={14} className="mr-1.5" /> {saving ? 'Saving...' : 'Save About Page'}
                  </Button>
                </div>
              </TabsContent>

              {/* GALLERY TAB */}
              <TabsContent value="gallery" className="space-y-4 mt-0">
                <Section title="Gallery Page Header">
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div><Label className="text-xs">Label</Label>
                      <Input value={pg('gallery').page_label || ''} onChange={e => update('gallery', 'page_label', e.target.value)}
                        data-testid="gallery-label" /></div>
                    <div><Label className="text-xs">Title</Label>
                      <Input value={pg('gallery').page_title || ''} onChange={e => update('gallery', 'page_title', e.target.value)}
                        data-testid="gallery-title" /></div>
                  </div>
                </Section>
                <div className="flex justify-end sticky bottom-4 pt-2">
                  <Button onClick={() => handleSave('gallery')} disabled={saving} className="bg-accent hover:bg-accent/90"
                    data-testid="save-gallery-btn">
                    <Save size={14} className="mr-1.5" /> {saving ? 'Saving...' : 'Save Gallery Page'}
                  </Button>
                </div>
              </TabsContent>

              {/* EXHIBITIONS TAB */}
              <TabsContent value="exhibitions" className="space-y-4 mt-0">
                <Section title="Exhibitions Page Header">
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div><Label className="text-xs">Label</Label>
                      <Input value={pg('exhibitions').page_label || ''} onChange={e => update('exhibitions', 'page_label', e.target.value)}
                        data-testid="exhibitions-label" /></div>
                    <div><Label className="text-xs">Title</Label>
                      <Input value={pg('exhibitions').page_title || ''} onChange={e => update('exhibitions', 'page_title', e.target.value)}
                        data-testid="exhibitions-title" /></div>
                  </div>
                  <div><Label className="text-xs">Description</Label>
                    <Textarea value={pg('exhibitions').page_description || ''} onChange={e => update('exhibitions', 'page_description', e.target.value)}
                      rows={2} data-testid="exhibitions-description" /></div>
                </Section>
                <div className="flex justify-end sticky bottom-4 pt-2">
                  <Button onClick={() => handleSave('exhibitions')} disabled={saving} className="bg-accent hover:bg-accent/90"
                    data-testid="save-exhibitions-btn">
                    <Save size={14} className="mr-1.5" /> {saving ? 'Saving...' : 'Save Exhibitions'}
                  </Button>
                </div>
              </TabsContent>

              {/* PRESS TAB */}
              <TabsContent value="press" className="space-y-4 mt-0">
                <Section title="Press Page Header">
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div><Label className="text-xs">Label</Label>
                      <Input value={pg('press').page_label || ''} onChange={e => update('press', 'page_label', e.target.value)}
                        data-testid="press-label" /></div>
                    <div><Label className="text-xs">Title</Label>
                      <Input value={pg('press').page_title || ''} onChange={e => update('press', 'page_title', e.target.value)}
                        data-testid="press-title" /></div>
                  </div>
                  <div><Label className="text-xs">Description</Label>
                    <Textarea value={pg('press').page_description || ''} onChange={e => update('press', 'page_description', e.target.value)}
                      rows={2} data-testid="press-description" /></div>
                </Section>
                <div className="flex justify-end sticky bottom-4 pt-2">
                  <Button onClick={() => handleSave('press')} disabled={saving} className="bg-accent hover:bg-accent/90"
                    data-testid="save-press-btn">
                    <Save size={14} className="mr-1.5" /> {saving ? 'Saving...' : 'Save Press Page'}
                  </Button>
                </div>
              </TabsContent>

              {/* FOOTER TAB */}
              <TabsContent value="footer" className="space-y-4 mt-0">
                <Section title="Footer Content">
                  <div><Label className="text-xs">About Text</Label>
                    <Textarea value={pg('footer').about_text || ''} onChange={e => update('footer', 'about_text', e.target.value)}
                      rows={2} data-testid="footer-about-text" /></div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div><Label className="text-xs">Newsletter Title</Label>
                      <Input value={pg('footer').newsletter_title || ''} onChange={e => update('footer', 'newsletter_title', e.target.value)}
                        data-testid="footer-newsletter-title" /></div>
                    <div><Label className="text-xs">Newsletter Text</Label>
                      <Input value={pg('footer').newsletter_text || ''} onChange={e => update('footer', 'newsletter_text', e.target.value)}
                        data-testid="footer-newsletter-text" /></div>
                  </div>
                </Section>
                <div className="flex justify-end sticky bottom-4 pt-2">
                  <Button onClick={() => handleSave('footer')} disabled={saving} className="bg-accent hover:bg-accent/90"
                    data-testid="save-footer-btn">
                    <Save size={14} className="mr-1.5" /> {saving ? 'Saving...' : 'Save Footer'}
                  </Button>
                </div>
              </TabsContent>
            </div>

            {/* ─── PREVIEW PANEL ─── */}
            {showPreview && (
              <div className="w-[42%] flex-shrink-0 hidden lg:block" data-testid="preview-panel">
                <div className="sticky top-28 border border-border rounded-lg bg-background overflow-hidden shadow-sm"
                  style={{ maxHeight: 'calc(100vh - 8rem)' }}>
                  <PreviewPanel activeTab={activeTab} content={content} />
                </div>
              </div>
            )}
          </div>
        </Tabs>
      </div>
    </div>
  );
};

export default PageContentEditor;
